/**
 * Comment card — renders a single comment with its replies and actions.
 *
 * Action visibility is derived from loaded permissions.
 *
 * Jump follows the C3 matrix (see {@link resolveJumpAction}): a comment
 * anchored in the editor on screen scrolls there, a comment belonging to
 * another section's editor navigates to it, and a whole-document comment has
 * nowhere to go at all.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Sparkles, MessageSquare, MoreHorizontal, Pencil, Trash } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ComposerRow } from '@/components/drafting/CommentComposer'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ApiRequestError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { renderMentionsToNodes } from '@/lib/comment-mentions-render'
import { MentionEditor, type MentionEditorHandle } from '@/components/drafting/MentionEditor'
import { MentionInviteNotice } from '@/components/drafting/MentionInviteNotice'
import { useCommentsStore } from '@/stores/comments-store'
import { useMentionInviteFlow } from '@/hooks/useMentionInvites'
import { refreshBidMembers } from '@/hooks/useBidMembers'
import { isThreadUnread, unreadReference } from '@/lib/comment-unread'
import { useUnifiedAiPanelStore } from '@/stores/unified-ai-panel-store'
import { useAgentStore } from '@/stores/agent-store'
import { useAuthStore } from '@/stores/auth-store'
import { MemberAvatar, type BidMemberInfo } from '@/components/ui/owner-assignment'
import { absoluteTime, relativeTime } from '@/lib/datetime'
import type { Comment, CommentTargetType } from '@/lib/comments-api'
import { jumpToComment } from '@/lib/commentAnchor'

/**
 * Where the editor showing this comment list sits relative to the comment.
 * Supplied by the page that owns the route — the card knows nothing about URLs.
 */
export interface CommentJumpContext {
  /**
   * `composite` — this editor holds every section (the document editor), so a
   * jump never leaves the page. `single` — one section per editor (section /
   * questionnaire), so another section's comment needs a route change.
   */
  editorMode: 'composite' | 'single'
  /** The section this editor is showing. Null in composite mode. */
  currentSectionKey: string | null
  /** Route to another section's editor, or null when that key has no route. */
  buildSectionPath: (sectionKey: string) => string | null
  navigate: (path: string) => void
}

/** What Jump does for a given comment — `none` also means "render no button". */
type CommentJumpAction =
  | { kind: 'none' }
  | { kind: 'in-document' }
  | { kind: 'navigate'; path: string }

/**
 * The C3 jump matrix, as one pure decision:
 *
 * 1. Anchored in the editor on screen → scroll to it (the editor degrades a
 *    drifted or section-level anchor to a section scroll — see
 *    `applyCommentJump`), which is why an absent text range never hides Jump.
 * 2. Composite editor → every section is on screen, so it is always case 1.
 * 3. Single editor, another section → navigate to that section's editor and let
 *    its deep-link hook take over. Never quote-search foreign text here.
 * 4. Whole-document comment (no section), or a section with no route → nothing
 *    to jump to.
 *
 * A whole-object thread (a compliance requirement, a risk-register item)
 * short-circuits ahead of all of it: it hangs off a table row, so no editor
 * anywhere is its home — and a stray `sectionId` on one must not route the
 * reader into a draft it has no relationship with.
 */
function resolveJumpAction(
  comment: Pick<Comment, 'sectionId'>,
  context: CommentJumpContext | null,
  targetType: CommentTargetType,
): CommentJumpAction {
  if (targetType !== 'draft') return { kind: 'none' }
  if (comment.sectionId == null) return { kind: 'none' }
  if (!context || context.editorMode === 'composite') return { kind: 'in-document' }
  if (comment.sectionId === context.currentSectionKey) return { kind: 'in-document' }
  const path = context.buildSectionPath(comment.sectionId)
  return path ? { kind: 'navigate', path } : { kind: 'none' }
}

interface CommentCardProps {
  comment: Comment
  bidId: string
  /** Draft artifact id, or extraction-item id on a whole-object target type. */
  targetId: string
  /** Which comment family this thread belongs to. Defaults to the draft one. */
  targetType?: CommentTargetType
  /** Bid members offered as @mention suggestions in the reply box. */
  bidMembers?: BidMemberInfo[] | undefined
  /** Omitted (tests, embedded uses) ⇒ every anchored comment jumps in-document. */
  jumpContext?: CommentJumpContext | undefined
}

function authorLabel(
  createdBy: string,
  createdByName: string | null,
  currentUserId: string | null | undefined,
): string {
  if (currentUserId && createdBy === currentUserId) return 'You'
  return createdByName?.trim() || 'Unknown'
}

/**
 * The server locks an item for editing once the counterparty has read it and
 * answers 403 carrying this distinct code. The raw message ("This item is
 * locked because…") reads like a system fault; name the actual reason instead.
 * Only THIS code gets the lock wording — a finalized artifact or a revoked
 * membership also 403s, and calling those "seen by the other side" is a lie.
 */
const COUNTERPARTY_LOCK_CODE = 'COMMENT_COUNTERPARTY_LOCK'

/** Identifies which body the card is editing / about to delete. */
type MutationTarget = { kind: 'comment' } | { kind: 'reply'; replyId: string }

const counterpartyLockMessage = (kind: MutationTarget['kind']) =>
  kind === 'reply'
    ? 'This reply has been seen by the other side and can no longer be edited'
    : 'This comment has been seen by the other side and can no longer be edited'

const permissionLostMessage = (kind: MutationTarget['kind']) =>
  `You no longer have permission to change this ${kind}`

export function CommentCard({
  comment,
  bidId,
  targetId,
  targetType = 'draft',
  bidMembers = [],
  jumpContext,
}: CommentCardProps) {
  const permissionsMap = useCommentsStore((s) => s.permissions)
  const fixActionsByArtifact = useCommentsStore((s) => s.fixActionsByTargetId)
  const fixActions = fixActionsByArtifact[targetId]?.[comment.id] ?? null
  const resolve = useCommentsStore((s) => s.resolve)
  const reopen = useCommentsStore((s) => s.reopen)
  const addReply = useCommentsStore((s) => s.addReply)
  const update = useCommentsStore((s) => s.update)
  const remove = useCommentsStore((s) => s.remove)
  const updateReply = useCommentsStore((s) => s.updateReply)
  const removeReply = useCommentsStore((s) => s.removeReply)
  const resolveWithAI = useCommentsStore((s) => s.resolveWithAI)
  const loadPermissions = useCommentsStore((s) => s.loadPermissions)
  const focusedCommentId = useCommentsStore((s) => s.focusedCommentId)
  const focusComment = useCommentsStore((s) => s.focusComment)
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const unreadCutLine = useCommentsStore((s) => unreadReference(s, targetId))
  const isNew = isThreadUnread(comment, unreadCutLine, currentUserId)

  const [replyOpen, setReplyOpen] = useState(false)
  const [replyEmpty, setReplyEmpty] = useState(true)
  const [busy, setBusy] = useState<null | 'resolve' | 'reopen' | 'reply' | 'ai' | 'edit' | 'delete'>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<MutationTarget | null>(null)
  const [editEmpty, setEditEmpty] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<MutationTarget | null>(null)
  const replyEditorRef = useRef<MentionEditorHandle>(null)
  const editEditorRef = useRef<MentionEditorHandle>(null)
  // Only while the reply box is open, so a long thread list fetches nothing.
  const replyInvites = useMentionInviteFlow(bidMembers, targetType === 'draft' && replyOpen)

  // Comments carry their author's name, so an initials avatar renders even for
  // someone who has since left the bid. `bidMembers` only upgrades that to a
  // real profile picture for the people still on it.
  const avatarUrlByUserId = useMemo(
    () => new Map(bidMembers.map((m) => [m.userId, m.avatarUrl])),
    [bidMembers],
  )
  const avatarUser = (userId: string, fullName: string | null) => ({
    userId,
    fullName,
    avatarUrl: avatarUrlByUserId.get(userId) ?? null,
  })

  const permsKey = `${targetId}:${comment.sectionId ?? '__artifact__'}`
  const perms = permissionsMap[permsKey]
  const isFocused = focusedCommentId === comment.id

  // Edit/delete rights are artifact-wide, so they read the artifact-level
  // permissions entry (loaded by CommentsTab) rather than this comment's
  // section entry — `effectiveRole === 'owner'` and `artifactStatus` are set
  // from bid ownership and artifact state, which no section can change.
  const artifactPerms = permissionsMap[`${targetId}:__artifact__`]
  const isBidOwner = artifactPerms?.effectiveRole === 'owner'
  const isFinalized = artifactPerms?.artifactStatus === 'finalized'

  /**
   * Mirrors the server's `requireCommentEdit`: author, or bid owner/org admin.
   * A finalized artifact freezes everything — note this is deliberately NOT
   * `canCreate`, which is false for many unrelated reasons (e.g. a reviewer on
   * a section that hasn't been handed off) that have no bearing on editing
   * something you already wrote.
   *
   * `artifactStatus` describes a *draft* artifact, so a whole-object thread
   * ignores it: a finalized draft elsewhere in the bid has no bearing on who
   * may edit a note left on a compliance or risk row.
   */
  const canManage = (createdBy: string): boolean =>
    targetType !== 'draft'
      ? createdBy === currentUserId || isBidOwner
      : !isFinalized && (createdBy === currentUserId || isBidOwner)

  // Load permissions for this comment's section if missing.
  useEffect(() => {
    if (!perms) {
      loadPermissions(bidId, targetId, comment.sectionId, targetType).catch(() => {})
    }
  }, [perms, bidId, targetId, targetType, comment.sectionId, loadPermissions])

  const canReply = perms?.canReply ?? false
  const canResolve = perms?.canResolve ?? false
  const canResolveWithAI = perms?.canResolveWithAI ?? false

  const handleResolve = async () => {
    if (busy) return
    setBusy('resolve')
    setError(null)
    try {
      await resolve(bidId, targetId, comment.id, targetType)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve')
    } finally {
      setBusy(null)
    }
  }

  const handleReopen = async () => {
    if (busy) return
    setBusy('reopen')
    setError(null)
    try {
      await reopen(bidId, targetId, comment.id, targetType)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reopen')
    } finally {
      setBusy(null)
    }
  }

  // `body` carries `@[Name](user:<id>)` tokens straight from the editor — the
  // backend parses those to decide who gets a mention notification, so it must
  // never be replaced with the rendered text.
  // `confirmed` is the prompt's "Add and reply" — see CommentComposer.
  const handleReply = async (body: string, confirmed = false) => {
    const trimmed = body.trim()
    if (!trimmed || busy) return
    const addToBid = replyInvites.prepare(trimmed, confirmed)
    if (addToBid === null) return
    setBusy('reply')
    setError(null)
    try {
      if (addToBid.length > 0) {
        await addReply(bidId, targetId, comment.id, trimmed, targetType, addToBid)
        refreshBidMembers(bidId)
      } else {
        await addReply(bidId, targetId, comment.id, trimmed, targetType)
      }
      replyEditorRef.current?.clear()
      // Must reset `replyEmpty` too: the next composer mounts already-empty and
      // MentionEditor only reports empty-state *transitions*, so a stale `false`
      // would leave Send enabled over a blank reply box.
      closeReply()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add reply')
    } finally {
      setBusy(null)
    }
  }

  const closeReply = () => {
    setReplyOpen(false)
    setReplyEmpty(true)
    replyInvites.reset()
  }

  const handleResolveWithAI = async () => {
    if (busy) return
    setBusy('ai')
    setError(null)
    try {
      const result = await resolveWithAI(bidId, targetId, comment.id)
      // Hand off to the Ask AI (`write`) tab, same pattern as Review → Fix with AI.
      useUnifiedAiPanelStore.getState().setPendingSeed(result.threadSeed, result.threadId)
      useUnifiedAiPanelStore.getState().setTab('write')
      try {
        await useAgentStore.getState().switchThread(result.threadId)
      } catch {
        // Non-fatal; the panel still shows the seed input.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start AI resolve')
    } finally {
      setBusy(null)
    }
  }

  const jumpAction = resolveJumpAction(comment, jumpContext ?? null, targetType)

  const handleJump = () => {
    if (jumpAction.kind === 'navigate') {
      // The destination page's deep-link hook focuses the comment once its list
      // has loaded — the editor we're leaving can't do that for it.
      jumpContext?.navigate(
        `${jumpAction.path}?commentId=${encodeURIComponent(comment.id)}`,
      )
      return
    }
    focusComment(comment.id)
    jumpToComment(comment)
  }

  // Two different 403s land here. The counterparty-seen lock is the expected
  // one and gets its plain-language explanation; anything else (artifact
  // finalized, membership revoked mid-session) is a genuine permission loss
  // and must not be mislabelled as the lock.
  const reportMutationError = (
    err: unknown,
    fallback: string,
    kind: MutationTarget['kind'],
  ) => {
    if (err instanceof ApiRequestError && err.statusCode === 403) {
      toast.error(
        err.code === COUNTERPARTY_LOCK_CODE
          ? counterpartyLockMessage(kind)
          : permissionLostMessage(kind),
      )
      return
    }
    setError(err instanceof Error ? err.message : fallback)
  }

  const startEdit = (target: MutationTarget) => {
    setEditing(target)
    // The editor mounts prefilled, so it is never empty on entry; the seeded
    // MentionEditor confirms this on mount via onEmptyChange.
    setEditEmpty(false)
    setError(null)
  }

  // `body` keeps its `@[Name](user:<id>)` tokens — the backend re-parses them to
  // decide who the edit newly mentions.
  const handleSaveEdit = async (body: string) => {
    const target = editing
    if (!target) return
    const trimmed = body.trim()
    if (!trimmed || busy) return
    setBusy('edit')
    setError(null)
    try {
      if (target.kind === 'reply') {
        await updateReply(bidId, targetId, comment.id, target.replyId, trimmed, targetType)
      } else {
        await update(bidId, targetId, comment.id, trimmed, targetType)
      }
      setEditing(null)
    } catch (err) {
      reportMutationError(err, 'Failed to save changes', target.kind)
    } finally {
      setBusy(null)
    }
  }

  const handleConfirmDelete = async () => {
    const target = pendingDelete
    if (!target || busy) return
    setBusy('delete')
    setError(null)
    try {
      if (target.kind === 'reply') {
        await removeReply(bidId, targetId, comment.id, target.replyId, targetType)
      } else {
        await remove(bidId, targetId, comment.id, targetType)
      }
    } catch (err) {
      reportMutationError(err, 'Failed to delete', target.kind)
    } finally {
      setBusy(null)
      setPendingDelete(null)
    }
  }

  // Editing an existing body keeps its explicit Save / Cancel row — an in-place
  // rewrite deserves a deliberate commit, unlike posting a new reply.
  const renderEditor = (target: MutationTarget, initialValue: string) => (
    <div className="flex flex-col gap-1.5">
      <div className="rounded-xl border border-input bg-card focus-within:ring-1 focus-within:ring-ring">
        <MentionEditor
          // `initialValue` seeds the document once, so swapping which body is
          // being edited has to remount the editor.
          key={target.kind === 'reply' ? `edit-reply-${target.replyId}` : `edit-${comment.id}`}
          ref={editEditorRef}
          bidMembers={bidMembers}
          initialValue={initialValue}
          placeholder="Edit…"
          compact
          onSubmit={(body) => void handleSaveEdit(body)}
          onCancel={() => setEditing(null)}
          onEmptyChange={setEditEmpty}
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditing(null)}
          disabled={busy === 'edit'}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => void handleSaveEdit(editEditorRef.current?.getBody() ?? '')}
          disabled={busy === 'edit' || editEmpty}
        >
          {busy === 'edit' ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  )

  return (
    <div
      data-comment-id={comment.id}
      className={cn(
        // No box at rest. The negative margin plus padding only exists so the
        // focus wash and the hover reveal have a shape to live in.
        'group/comment flex flex-col gap-2 rounded-lg px-2 py-1.5 -mx-2 transition-colors',
        comment.status === 'resolved' && 'opacity-60',
        // Deep-link landing: a soft brand wash rather than a ring, which would
        // reintroduce the border this design removes.
        isFocused && 'bg-[hsl(var(--brand)/0.06)]',
      )}
    >
      <div className="flex gap-2.5">
        <MemberAvatar size="sm" user={avatarUser(comment.createdBy, comment.createdByName)} />
        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
          {/* Header line: who, when, and the quiet state chips */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {authorLabel(comment.createdBy, comment.createdByName, currentUserId)}
            </span>
            <span className="text-meta">
              <span title={absoluteTime(comment.createdAt)}>{relativeTime(comment.createdAt)}</span>
              {comment.editedAt && <span title={absoluteTime(comment.editedAt)}> · edited</span>}
            </span>
            {/* Anchor scope: text-anchored comments show their quote below
                instead. A whole-object thread has no document to be anchored
                in, so the scope label is meaningless there. */}
            {targetType === 'draft' && !comment.quotedText && (
              <span className="text-meta">
                {comment.sectionId != null ? 'Section-level' : 'Whole document'}
              </span>
            )}
            {/* "New" is the one chip that still has to pop; everything below it
                is quiet inline text. */}
            {isNew && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0 bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]"
              >
                New
              </Badge>
            )}
            {comment.sourceFindingRef && (
              <span className="text-[11px] text-muted-foreground">
                From AI review · {comment.sourceFindingRef.criterion}
              </span>
            )}
            {fixActions?.status === 'failed' && (
              <span className="text-[11px] text-destructive">AI fix failed</span>
            )}
            {comment.postedAfterApproval && (
              <span className="text-[11px] text-muted-foreground">Post-approval</span>
            )}
            {comment.preHandoffNote && (
              <span className="text-[11px] text-muted-foreground">Pre-handoff</span>
            )}
            {comment.status === 'resolved' && (
              <span className="text-[11px] text-muted-foreground">Resolved</span>
            )}
            {canManage(comment.createdBy) && (
              <ActionsMenu
                label="Comment actions"
                revealClassName="group-hover/comment:opacity-100"
                // `self-center` because the row is baseline-aligned and an
                // icon-only button has no text baseline to align to.
                className="ml-auto self-center -my-1"
                onEdit={() => startEdit({ kind: 'comment' })}
                onDelete={() => setPendingDelete({ kind: 'comment' })}
              />
            )}
          </div>

          {/* Anchor visualization */}
          {comment.quotedText && (
            <div className="text-xs italic border-l-2 border-[hsl(var(--brand-light))] pl-2 text-muted-foreground line-clamp-2">
              “{comment.quotedText}”
            </div>
          )}

          {/* Body */}
          {editing?.kind === 'comment' ? (
            renderEditor({ kind: 'comment' }, comment.body)
          ) : (
            <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
              {renderMentionsToNodes(comment.body, currentUserId ?? undefined)}
            </div>
          )}

          {/* Replies — the avatar gutter is already the indent, so no left rule */}
          {comment.replies.length > 0 && (
            <div className="flex flex-col gap-2.5 mt-1">
              {comment.replies.map((reply) => (
                <div key={reply.id} className="group/reply flex gap-2.5">
                  {reply.kind === 'ai_fix' ? (
                    // The sparkle stands in for the avatar — an AI fix has no
                    // human author to show.
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                      <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--brand))]" />
                    </div>
                  ) : (
                    <MemberAvatar
                      size="xs"
                      user={avatarUser(reply.createdBy, reply.createdByName)}
                    />
                  )}
                  <div className="min-w-0 flex-1 flex flex-col gap-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {reply.kind === 'ai_fix' ? (
                        <span className="text-sm font-medium text-[hsl(var(--brand))]">AI fix</span>
                      ) : (
                        <span className="text-sm font-medium">
                          {authorLabel(reply.createdBy, reply.createdByName, currentUserId)}
                        </span>
                      )}
                      <span className="text-meta">
                        <span title={absoluteTime(reply.createdAt)}>
                          {relativeTime(reply.createdAt)}
                        </span>
                        {reply.editedAt && (
                          <span title={absoluteTime(reply.editedAt)}> · edited</span>
                        )}
                      </span>
                      {/* `ai_fix` replies are the audit trail of what the AI did
                          — the server refuses to edit them, so no menu. */}
                      {reply.kind !== 'ai_fix' && canManage(reply.createdBy) && (
                        <ActionsMenu
                          label="Reply actions"
                          revealClassName="group-hover/reply:opacity-100"
                          className="ml-auto self-center -my-1"
                          onEdit={() => startEdit({ kind: 'reply', replyId: reply.id })}
                          onDelete={() => setPendingDelete({ kind: 'reply', replyId: reply.id })}
                        />
                      )}
                    </div>
                    {editing?.kind === 'reply' && editing.replyId === reply.id ? (
                      renderEditor({ kind: 'reply', replyId: reply.id }, reply.body)
                    ) : (
                      <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {renderMentionsToNodes(reply.body, currentUserId ?? undefined)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reply composer — Escape cancels (MentionEditor), so no Cancel button */}
          {replyOpen && (
            <ComposerRow
              sendLabel="Send reply"
              disabled={busy === 'reply' || replyEmpty}
              onSend={() => void handleReply(replyEditorRef.current?.getBody() ?? '')}
            >
              <MentionEditor
                ref={replyEditorRef}
                bidMembers={bidMembers}
                nonMemberCandidates={replyInvites.nonMemberCandidates}
                onBodyChange={replyInvites.onBodyChange}
                placeholder="Reply…"
                compact
                onSubmit={(body) => void handleReply(body)}
                onCancel={closeReply}
                onEmptyChange={setReplyEmpty}
              />
            </ComposerRow>
          )}
          {replyOpen && (
            <MentionInviteNotice
              pendingAdd={replyInvites.pendingAdd}
              unreachable={replyInvites.unreachable}
              confirmLabel="Add and reply"
              busy={busy === 'reply'}
              onConfirm={() => void handleReply(replyInvites.body, true)}
              onCancel={() => {
                replyInvites.cancel()
                replyEditorRef.current?.focus()
              }}
            />
          )}

          {error && <div className="text-xs text-destructive">{error}</div>}

          {/* Action row — quiet text actions, not buttons */}
          <div className="flex items-center flex-wrap gap-1.5">
            {jumpAction.kind !== 'none' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleJump}
              >
                Jump
              </Button>
            )}
            {canReply && comment.status === 'open' && !replyOpen && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs gap-1 text-muted-foreground hover:text-foreground"
                onClick={() => setReplyOpen(true)}
              >
                <MessageSquare className="h-3 w-3" />
                Reply
              </Button>
            )}
            {comment.status === 'open' && canResolveWithAI && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-xs gap-1 text-[hsl(var(--ai-accent-text))]"
                onClick={() => void handleResolveWithAI()}
                disabled={busy !== null}
              >
                <Sparkles className="h-3 w-3" />
                Resolve with AI
              </Button>
            )}
            {comment.status === 'open'
              ? canResolve && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-xs ml-auto text-muted-foreground hover:text-foreground"
                    onClick={() => void handleResolve()}
                    disabled={busy !== null}
                  >
                    {busy === 'resolve' ? 'Resolving…' : 'Mark resolved'}
                  </Button>
                )
              : canResolve && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-xs ml-auto text-muted-foreground hover:text-foreground"
                    onClick={() => void handleReopen()}
                    disabled={busy !== null}
                  >
                    {busy === 'reopen' ? 'Reopening…' : 'Reopen'}
                  </Button>
                )}
          </div>
        </div>
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.kind === 'reply' ? 'Delete this reply?' : 'Delete this comment?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.kind === 'reply'
                ? 'This cannot be undone.'
                : 'Its replies are deleted with it.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Gated on ANY busy state, not just `delete`: `handleConfirmDelete`
              early-returns while another mutation is in flight, so a narrower
              gate would leave a confirm button that looks live and does
              nothing. */}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={busy !== null}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy === 'delete' ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/**
 * Overflow menu for a comment or reply. Progressive disclosure: invisible at
 * rest, revealed on hover of the row it belongs to (`revealClassName`), on
 * keyboard focus, and while open.
 */
function ActionsMenu({
  label,
  revealClassName,
  className,
  onEdit,
  onDelete,
}: {
  label: string
  revealClassName: string
  className?: string
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          className={cn(
            'h-6 w-6 shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 data-[state=open]:opacity-100',
            revealClassName,
            className,
          )}
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-4 w-4 mr-2 text-muted-foreground" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onDelete}
          className="text-muted-foreground focus:text-destructive"
        >
          <Trash className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
